import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Plus, Play, Pause, X, Megaphone, AlertTriangle, ShieldCheck } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  scheduled: "bg-indigo-100 text-indigo-700",
  running: "bg-emerald-100 text-emerald-700 animate-pulse",
  paused: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-700",
  failed: "bg-rose-100 text-rose-800",
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", segment_id: "", template_id: "" });
  const [confirmData, setConfirmData] = useState(null); // { campaign_id, preview }

  const load = async () => setItems((await api.get("/campaigns")).data);
  useEffect(() => {
    load();
    api.get("/segments").then(r=>setSegments(r.data));
    api.get("/templates").then(r=>setTemplates(r.data));
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const create = async () => {
    try { await api.post("/campaigns", f); toast.success("Campaign created"); setOpen(false); setF({name:"",segment_id:"",template_id:""}); load(); }
    catch { toast.error("Failed"); }
  };
  const openConfirm = async (id) => {
    try {
      const r = await api.post(`/campaigns/${id}/preview`);
      setConfirmData({ id, ...r.data });
    } catch { toast.error("Preview failed"); }
  };
  const start = async (id) => {
    await api.post(`/campaigns/${id}/start`);
    toast.success("Campaign started");
    setConfirmData(null); load();
  };
  const pause = async (id) => { await api.post(`/campaigns/${id}/pause`); load(); };
  const cancel = async (id) => { await api.post(`/campaigns/${id}/cancel`); load(); };

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Campaigns</h1>
          <p className="text-sm text-slate-500">Bulk WhatsApp campaigns via configured provider</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-campaign-button"><Plus className="w-4 h-4 mr-1"/>New Campaign</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>New campaign</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Campaign name</Label><Input value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} data-testid="camp-name-input"/></div>
              <div><Label>Segment</Label>
                <Select value={f.segment_id} onValueChange={(v)=>setF({...f,segment_id:v})}>
                  <SelectTrigger data-testid="camp-segment-select"><SelectValue placeholder="Choose segment"/></SelectTrigger>
                  <SelectContent className="bg-white">
                    {segments.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.contact_count})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Template</Label>
                <Select value={f.template_id} onValueChange={(v)=>setF({...f,template_id:v})}>
                  <SelectTrigger data-testid="camp-template-select"><SelectValue placeholder="Choose template"/></SelectTrigger>
                  <SelectContent className="bg-white">
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.status} · {t.source || "mock"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-campaign-button">Create as Draft</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((c) => {
          const m = c.metrics || {};
          const pct = m.recipients ? Math.round((m.sent / m.recipients) * 100) : 0;
          return (
            <Card key={c.id} className="p-5 border-slate-200" data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-700"><Megaphone className="w-5 h-5"/></div>
                  <div>
                    <div className="font-semibold text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <Badge className={STATUS_STYLE[c.status]}>{c.status}</Badge>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Progress</span><span>{m.sent || 0}/{m.recipients || 0}</span>
                </div>
                <Progress value={pct} className="h-2"/>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                {[["Sent",m.sent],["Delivered",m.delivered],["Read",m.read],["Replies",m.replies]].map(([l,v]) => (
                  <div key={l} className="p-2 bg-slate-50 rounded-lg">
                    <div className="text-lg font-bold text-slate-900">{v||0}</div>
                    <div className="text-[10px] text-slate-500 uppercase">{l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                {["draft","paused","scheduled"].includes(c.status) && <Button size="sm" onClick={()=>openConfirm(c.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`start-camp-${c.id}`}><Play className="w-3 h-3 mr-1"/>Start</Button>}
                {c.status === "running" && <Button size="sm" variant="outline" onClick={()=>pause(c.id)} data-testid={`pause-camp-${c.id}`}><Pause className="w-3 h-3 mr-1"/>Pause</Button>}
                {["running","paused","scheduled"].includes(c.status) && <Button size="sm" variant="outline" onClick={()=>cancel(c.id)} data-testid={`cancel-camp-${c.id}`}><X className="w-3 h-3 mr-1"/>Cancel</Button>}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!confirmData} onOpenChange={(v)=>!v && setConfirmData(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="campaign-confirm-dialog">
          <DialogHeader><DialogTitle>Confirm campaign send</DialogTitle></DialogHeader>
          {confirmData && (
            <div className="space-y-3 text-sm">
              <div className={`p-3 rounded-lg border ${confirmData.mode==="live" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
                <div className="font-semibold flex items-center gap-1">
                  {confirmData.mode==="live" ? <><ShieldCheck className="w-4 h-4"/>LIVE MODE — Real WhatsApp messages via Meta</> : <>MOCK MODE — Simulated only</>}
                </div>
              </div>
              <Row k="Campaign" v={confirmData.campaign?.name}/>
              <Row k="Template" v={confirmData.template ? `${confirmData.template.name} · ${confirmData.template.status} · ${confirmData.template.source}` : "—"}/>
              <Row k="Provider" v={confirmData.provider}/>
              <Row k="Recipients total" v={confirmData.recipients_total}/>
              <Row k="Eligible (opted in)" v={confirmData.recipients_eligible}/>
              <Row k="Excluded (opted out)" v={confirmData.recipients_opted_out}/>
              {confirmData.warnings?.length > 0 && (
                <div className="p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-900 text-xs space-y-1">
                  <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4"/>Warnings</div>
                  {confirmData.warnings.map((w,i) => <div key={i}>• {w}</div>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setConfirmData(null)} data-testid="camp-confirm-cancel">Cancel</Button>
            <Button onClick={()=>start(confirmData.id)} disabled={confirmData?.recipients_eligible === 0}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="camp-confirm-send">
              Send to {confirmData?.recipients_eligible} contacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between border-b border-slate-100 pb-1"><span className="text-slate-500">{k}</span><span className="font-medium">{v}</span></div>;
}
